import type { SnapSettings, SnapValues } from '../design-tool';

/**
 * A snap setting that is on, with its value, and whether it is used. One that another setting
 * decides is not, e.g. the height of a line at a slope.
 */
export type SnapSettingStatus = { used: boolean } & (
  | { setting: 'height'; mode: 'absolute' | 'offset'; value: number }
  | { setting: 'angle'; mode: 'perpendicular' }
  | { setting: 'angle'; mode: 'custom'; value: number }
  | { setting: 'length'; mode: '2d' | '3d'; value: number }
  | { setting: 'length'; mode: 'cutFill' }
  | {
      setting: 'incline';
      mode: 'slopePercent' | 'slopeDegrees' | 'slopeRatio';
      /** The slope in percent, which every incline is stored as. */
      value: number;
    }
);

/**
 * What is snapped: a point, e.g. the first point of a line, the next point of a line, or an offset.
 */
export type SnapAction = 'point' | 'line' | 'offset';

/**
 * The snap settings of what is snapped, for the tooltip, see getSnapSettingsStatus.
 */
export type TooltipSnapSettings = { action: SnapAction; settings: SnapSettingStatus[] };

/**
 * Gets the snap settings that are on for what is snapped, in the order of the snapping section,
 * and whether each is used:
 * - A point only has a height. The other settings apply from the second point of a line.
 * - The incline sets the height of a line or an offset, so the height setting is not used, unless
 *   cut/fill extends it to that height.
 * - The angle only applies to lines, not to offsets.
 */
export function getSnapSettingsStatus(
  settings: SnapSettings,
  values: SnapValues,
  action: SnapAction,
): SnapSettingStatus[] {
  const { height, angle, length, incline } = settings;
  const statuses: SnapSettingStatus[] = [];

  if (height) {
    statuses.push({
      setting: 'height',
      mode: height,
      value: height === 'absolute' ? values.height : values.heightOffset,
      used: action === 'point' || !incline || length === 'cutFill',
    });
  }

  if (action === 'point') {
    return statuses;
  }

  if (angle) {
    const used = action === 'line';
    statuses.push(
      angle === 'custom'
        ? { setting: 'angle', mode: angle, value: values.angle, used }
        : { setting: 'angle', mode: angle, used },
    );
  }

  if (length) {
    statuses.push(
      length === 'cutFill'
        ? { setting: 'length', mode: length, used: true }
        : { setting: 'length', mode: length, value: values.length, used: true },
    );
  }

  if (incline) {
    statuses.push({ setting: 'incline', mode: incline, value: values.slope, used: true });
  }

  return statuses;
}
